import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { observer } from "mobx-react";
import { reaction } from "mobx";
import { useLocation } from "react-router-dom";
import updatesPanelStore, { ALL_UPDATES_URL } from "Stores/updatesPanelStore";
import copilotDrawerStore from "Stores/copilotDrawerStore";
import userDetail from "Stores/userDetail";
import { ReactComponent as SalesgearLogoIcon } from "Global/Header/Icons/salesgearLogo.svg";
import UpdatePopup from "./UpdatePopup";

export const MegaphoneGlyph = ({ size = 19 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M4 10h3l9-5v14l-9-5H4a1 1 0 0 1-1-1v-2a1 1 0 0 1 1-1z" />
    <path d="M7.5 14.3 9 19.5h2.6l-1.2-4.2" />
    <path d="M19 9.5a3.5 3.5 0 0 1 0 5" />
  </svg>
);

// Area label colour, so the feed scans by product area.
const AREA_TONE = {
  Copilot: "violet",
  Agents: "blue",
  Sequences: "teal",
  Contacts: "green",
  Accounts: "green",
  Prospecting: "indigo",
  Research: "indigo",
  Data: "amber",
  Imports: "amber",
  Inbox: "sky",
  Reports: "pink",
  Billing: "slate",
  Settings: "slate"
};
const TYPE_TONE = { "New feature": "new", Improvement: "improved", Fix: "fixed" };

// Posts shown per page; "Load more" reveals the next batch.
const PAGE = 6;

const timeAgo = iso => {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days < 1) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 30) {
    const weeks = Math.floor(days / 7);
    return weeks === 1 ? "a week ago" : `${weeks} weeks ago`;
  }
  const months = Math.floor(days / 30);
  return months <= 1 ? "a month ago" : `${months} months ago`;
};

const matches = (p, q) =>
  !q || [p.title, p.summary, p.area, p.type, ...p.points].join(" ").toLowerCase().includes(q);

/*
 * What's new: the latest published product updates, read from WordPress, in an
 * AnnounceKit-style floating panel -- inset from the viewport edges, a brand
 * gradient that fades into the body, a greeting, search, and one card per
 * update (cover image, type + area, one-line title, three-line summary). A card
 * opens the update in a popup (UpdatePopup) with what changed and "Try it now".
 * Closes on the dimmed page, a route change, or Escape (popup first).
 */
const UpdatesPanel = observer(() => {
  const { open, posts, loading, failed } = updatesPanelStore;
  const { pathname } = useLocation();
  const [shown, setShown] = useState(open);
  const [visible, setVisible] = useState(PAGE);
  const [query, setQuery] = useState("");
  // What counted as unread when the panel opened, so the markers stay put while
  // the user reads instead of vanishing the moment they are marked seen.
  const seenAtOpen = useRef(updatesPanelStore.seen);

  useEffect(() => {
    if (!open) return;
    setShown(true);
    setVisible(PAGE);
    setQuery("");
    seenAtOpen.current = updatesPanelStore.seen;
    updatesPanelStore.load().then(updatesPanelStore.markSeen);
  }, [open]);

  useEffect(
    () =>
      reaction(
        () => copilotDrawerStore.open,
        copilotOpen => copilotOpen && updatesPanelStore.close()
      ),
    []
  );

  useEffect(() => updatesPanelStore.close(), [pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = e => {
      if (e.key !== "Escape") return;
      if (updatesPanelStore.selected) updatesPanelStore.closePost();
      else updatesPanelStore.close();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  if (!shown) return null;

  const firstName = (userDetail?.userInfo?.firstName || "").trim();
  const unread = posts.filter(p => p.date > seenAtOpen.current).length;
  const q = query.trim().toLowerCase();
  const found = posts.filter(p => matches(p, q));
  // Search shows every match; browsing pages through with "Load more".
  const list = q ? found : found.slice(0, visible);

  return ReactDOM.createPortal(
    <>
      <div
        className={`updatesScrim${open ? "" : " closing"}`}
        aria-hidden="true"
        onClick={updatesPanelStore.close}
      />
      <aside
        className={`updatesSheet${open ? "" : " closing"}`}
        aria-label="What's new"
        onAnimationEnd={e => e.target === e.currentTarget && !open && setShown(false)}
      >
        <div className="updatesSheet__head">
          <div className="updatesSheet__topRow">
            <span className="updatesSheet__headIcon">
              <MegaphoneGlyph size={18} />
            </span>
            <button
              type="button"
              className="updatesSheet__close"
              aria-label="Close what's new"
              onClick={updatesPanelStore.close}
            >
              <i className="material-icons-outlined">close</i>
            </button>
          </div>
          <h3 className="updatesSheet__title">
            {firstName ? `Hi ${firstName}` : "Hi there"}
            <span aria-hidden="true"> 👋</span>
          </h3>
          <p className="updatesSheet__sub">
            {unread > 0
              ? `${unread} new update${unread === 1 ? "" : "s"} since your last visit.`
              : "Here's what's new in Salesgear."}
          </p>
          <label className="updatesSheet__search" htmlFor="updatesSearch">
            <i className="material-icons-outlined">search</i>
            <input
              id="updatesSearch"
              type="search"
              placeholder="Search updates"
              value={query}
              onChange={e => setQuery(e.target.value)}
              autoComplete="off"
            />
          </label>
        </div>

        <div className="updatesSheet__body">
          {loading &&
            [0, 1, 2].map(i => (
              <div key={i} className="updatesSheet__skeleton linear-background" />
            ))}

          {!loading && failed && (
            <div className="updatesSheet__empty">
              <span className="updatesSheet__emptyIcon">
                <MegaphoneGlyph size={20} />
              </span>
              <b>Updates didn't load</b>
              <span>Check your connection and try again.</span>
              <button type="button" className="updatesSheet__retry" onClick={updatesPanelStore.retry}>
                <i className="material-icons-outlined">refresh</i>
                Try again
              </button>
            </div>
          )}

          {!loading && !failed && !list.length && (
            <div className="updatesSheet__empty">
              <span className="updatesSheet__emptyIcon">
                <MegaphoneGlyph size={20} />
              </span>
              <b>{q ? "No updates match" : "Nothing new yet"}</b>
              <span>
                {q
                  ? "Try another word, like a feature or page name."
                  : "New features and fixes show up here as they ship."}
              </span>
            </div>
          )}

          {!loading &&
            list.map(p => {
              const isNew = p.date > seenAtOpen.current;
              return (
                <article key={p.id} className="updatesSheet__post">
                  {p.image && (
                    <button
                      type="button"
                      className="updatesSheet__cover"
                      tabIndex={-1}
                      aria-hidden="true"
                      onClick={() => updatesPanelStore.openPost(p)}
                    >
                      <img src={p.image} alt="" loading="lazy" />
                    </button>
                  )}
                  <button
                    type="button"
                    className="updatesSheet__toggle"
                    aria-haspopup="dialog"
                    onClick={() => updatesPanelStore.openPost(p)}
                  >
                    <span className="updatesSheet__meta">
                      {p.type && (
                        <span className={`updatesSheet__type type-${TYPE_TONE[p.type]}`}>
                          {p.type}
                        </span>
                      )}
                      <span className={`updatesSheet__label tone-${AREA_TONE[p.area] || "blue"}`}>
                        {p.area}
                      </span>
                      {isNew && <span className="updatesSheet__new">New</span>}
                    </span>
                    <span className="updatesSheet__name" title={p.title}>
                      {p.title}
                    </span>
                    {p.summary && <span className="updatesSheet__summary">{p.summary}</span>}
                  </button>

                  <div className="updatesSheet__sign">
                    <span className="updatesSheet__avatar">
                      <SalesgearLogoIcon />
                    </span>
                    <span className="updatesSheet__by">Salesgear team</span>
                    <span className="updatesSheet__when">
                      <i className="material-icons-outlined">schedule</i>
                      {timeAgo(p.date)}
                    </span>
                    <button
                      type="button"
                      className="updatesSheet__chevBtn"
                      aria-label="Open update"
                      onClick={() => updatesPanelStore.openPost(p)}
                    >
                      <i className="material-icons-outlined updatesSheet__chev">chevron_right</i>
                    </button>
                  </div>
                </article>
              );
            })}

          {!loading && !q && found.length > visible && (
            <button
              type="button"
              className="updatesSheet__loadMore"
              onClick={() => setVisible(v => v + PAGE)}
            >
              Load more
              <i className="material-icons-outlined">expand_more</i>
            </button>
          )}
        </div>

        <div className="updatesSheet__foot">
          <a href={ALL_UPDATES_URL} target="_blank" rel="noopener noreferrer">
            See all updates
            <i className="material-icons-outlined">open_in_new</i>
          </a>
        </div>
      </aside>
      <UpdatePopup typeTone={TYPE_TONE} areaTone={AREA_TONE} />
    </>,
    document.body
  );
});

export default UpdatesPanel;
